export interface StoredSsoLoginHint {
    domain: string;
    orgIdentifier: string;
    email?: string;
    updatedAt: string;
}

const SSO_LOGIN_HINT_KEY = 'notizy_last_sso_login';

const normalizeDomain = (domain: string): string => {
    try {
        return new URL(domain).origin;
    } catch (error) {
        return domain.trim();
    }
};

const isStoredSsoLoginHint = (value: unknown): value is StoredSsoLoginHint => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Partial<StoredSsoLoginHint>;
    return typeof candidate.domain === 'string'
        && typeof candidate.orgIdentifier === 'string'
        && typeof candidate.updatedAt === 'string'
        && (typeof candidate.email === 'undefined' || typeof candidate.email === 'string');
};

export const getStoredSsoLoginHint = (domain?: string): StoredSsoLoginHint | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const storedValue = window.localStorage.getItem(SSO_LOGIN_HINT_KEY);
    if (!storedValue) {
        return null;
    }

    try {
        const parsed = JSON.parse(storedValue);
        if (!isStoredSsoLoginHint(parsed)) {
            window.localStorage.removeItem(SSO_LOGIN_HINT_KEY);
            return null;
        }

        const normalizedStoredDomain = normalizeDomain(parsed.domain);
        if (domain && normalizedStoredDomain !== normalizeDomain(domain)) {
            return null;
        }

        return {
            ...parsed,
            domain: normalizedStoredDomain
        };
    } catch (error) {
        window.localStorage.removeItem(SSO_LOGIN_HINT_KEY);
        return null;
    }
};

export const storeSsoLoginHint = ({ domain, orgIdentifier, email }: Omit<StoredSsoLoginHint, 'updatedAt'>): void => {
    if (typeof window === 'undefined') {
        return;
    }

    const trimmedOrgIdentifier = orgIdentifier.trim();
    if (!trimmedOrgIdentifier) {
        return;
    }

    const normalizedEmail = email?.trim() || undefined;

    window.localStorage.setItem(SSO_LOGIN_HINT_KEY, JSON.stringify({
        domain: normalizeDomain(domain),
        orgIdentifier: trimmedOrgIdentifier,
        email: normalizedEmail,
        updatedAt: new Date().toISOString()
    }));
};

export const clearStoredSsoLoginHint = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    window.localStorage.removeItem(SSO_LOGIN_HINT_KEY);
};
